/* ============================================================
   Lagomlo — theme.js (Shopify theme)
   Vanilla, dependency-free runtime behavior:
   - placeholder fill + design studio boot (deferred script order)
   - sticky header shadow, mobile menu, cart drawer
   - cart AJAX (change qty / remove / refresh drawer / add via studio)
   - FAQ accordion, announcement dismiss, collection sort
   - design thumbnails on cart line items (JSON fallback parse)
   Exposed: window.LagomloCart
   ============================================================ */
(function (global) {
  'use strict';

  var routes = (global.Lagomlo && global.Lagomlo.routes) || {};
  var moneyFmt = new Intl.NumberFormat((global.Lagomlo && global.Lagomlo.money && global.Lagomlo.money.locale) || 'en-US', {
    style: 'currency',
    currency: (global.Lagomlo && global.Lagomlo.money && global.Lagomlo.money.currency) || 'USD'
  });

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ---------- Cart ---------- */

  var Cart = {
    drawer: null,
    panel: null,
    body: null,

    init: function () {
      this.drawer = qs('[data-cart-drawer]');
      if (!this.drawer) return;
      this.panel = qs('[data-cart-drawer-panel]', this.drawer) || qs('.cart-drawer__panel', this.drawer);
      this.body = qs('[data-cart-drawer-body]', this.drawer);

      var openers = qsa('[data-cart-open]');
      openers.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          Cart.open();
        });
      });
      var closers = qsa('[data-cart-close], [data-cart-overlay]');
      closers.forEach(function (el) {
        el.addEventListener('click', function () { Cart.close(); });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && Cart.drawer.classList.contains('is-open')) Cart.close();
      });

      // Drawer qty +/- and remove
      this.body.addEventListener('click', function (e) {
        var target = e.target.closest ? e.target.closest('[data-cart-change], [data-cart-remove]') : null;
        if (!target) return;
        var li = target.closest('[data-cart-line]');
        if (!li) return;
        var line = parseInt(li.getAttribute('data-cart-line'), 10) || 1;
        if (target.hasAttribute('data-cart-remove')) {
          e.preventDefault();
          Cart.changeQty(line, 0);
        } else if (target.hasAttribute('data-cart-change')) {
          e.preventDefault();
          var delta = parseInt(target.getAttribute('data-cart-change'), 10) || 0;
          var qty = parseInt(li.getAttribute('data-qty') || li.querySelector('[data-qty-value]').textContent, 10) || 1;
          Cart.changeQty(line, Math.max(0, qty + delta));
        }
      });
    },

    open: function () {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },

    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    changeQty: function (line, quantity) {
      if (!routes.cartChange) return;
      fetch(routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('bad status');
          return res.json();
        })
        .then(function () { return Cart.refresh(); })
        .catch(function () {
          window.location.reload();
        });
    },

    refresh: function () {
      if (!routes.cart) return Promise.resolve();
      return fetch(routes.cart + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          Cart.render(cart);
          Cart.updateCounts(cart.item_count);
          return cart;
        })
        .catch(function () { /* ignore */ });
    },

    afterAdd: function () {
      Cart.refresh().then(function () { Cart.open(); });
    },

    updateCounts: function (count) {
      qsa('[data-cart-count]').forEach(function (el) {
        el.textContent = String(count);
        el.classList.toggle('is-hidden', count === 0);
      });
    },

    render: function (cart) {
      if (!this.body) return;
      var items = cart.items || [];
      var list = qs('[data-cart-items]', this.body);
      var footer = qs('[data-cart-footer]', this.body);
      var empty = qs('[data-cart-empty]', this.body);
      var subtotalEl = qs('[data-cart-subtotal]', this.body);

      if (!items.length) {
        if (list) list.innerHTML = '';
        if (footer) footer.hidden = true;
        if (empty) empty.hidden = false;
        return;
      }

      var html = '';
      items.forEach(function (item, idx) {
        html += Cart.itemHtml(item, idx + 1);
      });
      if (list) list.innerHTML = html;
      if (footer) footer.hidden = false;
      if (empty) empty.hidden = true;
      if (subtotalEl) subtotalEl.textContent = escHtml(cart.items_subtotal_price);

      // design thumbnails + placeholders in freshly rendered markup
      qsa('[data-cart-line]', this.body).forEach(Cart.fillDesignThumbs);
      if (global.LagomloPlaceholder) global.LagomloPlaceholder.fill(this.body);
    },

    itemHtml: function (item, line) {
      var props = item.properties || {};
      var designImage = props['Design Image'] || '';
      var designSummary = props['Design Summary'] || '';
      var designJson = props['Design'] || '';
      var media;
      if (designImage) {
        media = '<img class="cart-line-item__thumb" src="' + escHtml(designImage) + '" alt="' + escHtml(item.product_title) + '" loading="lazy">';
      } else if (item.featured_image && item.featured_image.url) {
        media = '<img class="cart-line-item__thumb" src="' + escHtml(item.featured_image.url) + '&width=240" alt="' + escHtml(item.product_title) + '" loading="lazy">';
      } else {
        media = '<div class="cart-line-item__thumb cart-line-item__thumb--ph placeholder-image" data-placeholder="' + escHtml(item.product_handle || 'photo-blanket') + '" data-placeholder-title="' + escHtml(item.product_title) + '"></div>';
      }

      var isDesign = designImage || designJson;
      return '<li class="cart-line-item" data-cart-line="' + line + '" data-qty="' + item.quantity + '" data-design="' + escHtml(designJson) + '" data-design-image="' + escHtml(designImage) + '">'
        + '<div class="cart-line-item__media">' + media + '</div>'
        + '<div class="cart-line-item__details">'
        + '<p class="cart-line-item__title"><a href="' + escHtml(item.url || '#') + '">' + escHtml(item.product_title) + '</a></p>'
        + (item.variant_title && item.variant_title !== 'Default Title' ? '<p class="cart-line-item__variant">' + escHtml(item.variant_title) + '</p>' : '')
        + (isDesign ? '<p class="cart-line-item__design-badge">✨ Custom design</p>' : '')
        + (designSummary ? '<p class="cart-line-item__design-summary">' + escHtml(designSummary) + '</p>' : '')
        + '</div>'
        + '<div class="cart-line-item__meta">'
        + '<p class="cart-line-item__price">' + escHtml(item.final_line_price) + '</p>'
        + '<div class="cart-line-item__qty" data-qty-wrap>'
        + '<button type="button" class="cart-line-item__qty-btn" data-cart-change="-1" aria-label="Quantity">−</button>'
        + '<span class="cart-line-item__qty-value" data-qty-value>' + item.quantity + '</span>'
        + '<button type="button" class="cart-line-item__qty-btn" data-cart-change="1" aria-label="Quantity">+</button>'
        + '</div>'
        + '<button type="button" class="cart-line-item__remove" data-cart-remove aria-label="Remove">✕</button>'
        + '</div>'
        + '</li>';
    },

    fillDesignThumbs: function (li) {
      var image = li.getAttribute('data-design-image');
      var design = li.getAttribute('data-design');
      var media = qs('.cart-line-item__media', li);
      if (!media || image) return;
      if (!design) return;
      try {
        var parsed = JSON.parse(design);
        if (parsed && parsed.imageDataUrl) {
          var img = document.createElement('img');
          img.className = 'cart-line-item__thumb';
          img.src = parsed.imageDataUrl;
          img.alt = '';
          media.innerHTML = '';
          media.appendChild(img);
        }
      } catch (e) { /* not JSON — skip */ }
    }
  };

  /* ---------- Mobile menu ---------- */

  function initMobileMenu() {
    var menu = qs('[data-mobile-menu]');
    if (!menu) return;
    var openers = qsa('[data-mobile-menu-open]');
    var closers = qsa('[data-mobile-menu-close]');
    openers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        menu.classList.add('is-open');
        menu.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      });
    });
    closers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        menu.classList.remove('is-open');
        menu.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      });
    });
    qsa('.mobile-menu__link', menu).forEach(function (link) {
      link.addEventListener('click', function () {
        menu.classList.remove('is-open');
        menu.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      });
    });
  }

  /* ---------- FAQ accordion ---------- */

  function initFaq() {
    var faq = qs('[data-faq]');
    if (!faq) return;
    qsa('[data-faq-toggle]', faq).forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var item = toggle.closest('.faq__item');
        var answer = qs('[data-faq-answer]', item);
        var open = item.classList.contains('is-open');
        // close others (accordion behavior)
        qsa('.faq__item.is-open', faq).forEach(function (other) {
          other.classList.remove('is-open');
          qs('[data-faq-toggle]', other).setAttribute('aria-expanded', 'false');
          var otherAnswer = qs('[data-faq-answer]', other);
          if (otherAnswer) otherAnswer.hidden = true;
        });
        item.classList.toggle('is-open', !open);
        toggle.setAttribute('aria-expanded', String(!open));
        if (answer) answer.hidden = open;
      });
    });
  }

  /* ---------- Announcement bar ---------- */

  function initAnnouncement() {
    var bar = qs('[data-announcement-bar]');
    var close = qs('[data-announcement-close]');
    if (!bar || !close) return;
    close.addEventListener('click', function () { bar.hidden = true; });
  }

  /* ---------- Collection sort ---------- */

  function initCollectionSort() {
    var sort = qs('[data-collection-sort]');
    if (!sort) return;
    sort.addEventListener('change', function () {
      var url = new URL(window.location.href);
      if (sort.value) url.searchParams.set('sort_by', sort.value);
      else url.searchParams.delete('sort_by');
      window.location.href = url.toString();
    });
  }

  /* ---------- Cart page qty buttons ---------- */

  function initCartPageQty() {
    var cartForm = qs('.cart-template__form');
    if (!cartForm) return;
    qsa('[data-cart-change]', cartForm).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var li = btn.closest('[data-cart-line]');
        var input = li && qs('[data-qty-input]', li);
        if (!input) return;
        var delta = parseInt(btn.getAttribute('data-cart-change'), 10) || 0;
        var qty = parseInt(input.value, 10) || 1;
        input.value = String(Math.max(0, qty + delta));
      });
    });
    qsa('[data-qty-input]', cartForm).forEach(function (input) {
      input.addEventListener('change', function () {
        var val = parseInt(input.value, 10);
        if (isNaN(val) || val < 0) input.value = '0';
      });
    });
  }

  /* ---------- Sticky header shadow ---------- */

  function initHeader() {
    var header = qs('[data-site-header]');
    if (!header) return;
    var onScroll = function () {
      header.classList.toggle('site-header--scrolled', window.scrollY > 8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Design studio anchors ---------- */

  function initStudioAnchors() {
    qsa('[data-open-studio]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = qs('#design-studio');
        if (target) {
          // slight delay so the studio JS can build option selects first
          setTimeout(function () {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 60);
        }
      });
    });
  }

  /* ---------- Cart page design thumbnails (initial render) ---------- */

  function initDesignThumbs() {
    qsa('[data-cart-line]').forEach(Cart.fillDesignThumbs);
  }

  /* ---------- Login / recover toggle ---------- */

  function initLoginRecover() {
    var recover = qs('[data-recover]');
    var login = qs('[data-login]');
    if (!recover || !login) return;
    qsa('[data-recover-toggle]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var target = link.getAttribute('data-target');
        var showRecover = target === 'recover';
        recover.hidden = !showRecover;
        login.hidden = showRecover;
      });
    });
  }

  /* ---------- Addresses (add / edit / cancel / delete) ---------- */

  function initAddresses() {
    var addBtn = qs('[data-address-add]');
    var newForm = qs('[data-address-form="new"]');
    if (addBtn && newForm) {
      addBtn.addEventListener('click', function () { newForm.hidden = !newForm.hidden; });
    }

    qsa('[data-address-edit]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-address-edit');
        var form = qs('[data-address-form="' + id + '"]');
        if (form) form.hidden = !form.hidden;
      });
    });

    qsa('[data-address-cancel]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var form = btn.closest('[data-address-form]');
        if (form) form.hidden = true;
      });
    });

    qsa('[data-address-delete]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('[data-address-card]');
        var form = card && qs('[data-address-delete-form]', card);
        if (!form) return;
        var ok = window.confirm('Are you sure you want to delete this address?');
        if (ok) form.submit();
      });
    });
  }

  /* ---------- Scroll reveal (premium motion) ---------- */

  function initReveal() {
    // .reveal elements (manual) + homepage sections (auto)
    var els = qsa('.reveal, .template-index .section');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Category picker (全品类入口) ---------- */

  function initCategoryPicker() {
    var cards = qsa('[data-category-card]');
    if (!cards.length) return;
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        var key = card.getAttribute('data-category-card') || '';
        var studio = qs('[data-design-studio]');
        if (studio) {
          // On design-studio page: try to select a matching product chip, then scroll
          var chips = qsa('[data-studio-product-chip]');
          if (chips.length && key) {
            var match = chips.filter(function (c) {
              return c.textContent.toLowerCase().indexOf(key) !== -1;
            });
            if (match.length) match[0].click();
          }
          studio.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          // Navigate to design studio with category hash
          window.location.href = '/pages/design-studio' + (key ? '#' + key : '');
        }
      });
    });
  }

  /* ---------- Boot ---------- */

  function boot() {
    if (global.LagomloPlaceholder) global.LagomloPlaceholder.fill(document);
    if (global.LagomloStudio) global.LagomloStudio.init();

    initHeader();
    initMobileMenu();
    Cart.init();
    initFaq();
    initAnnouncement();
    initCollectionSort();
    initCartPageQty();
    initStudioAnchors();
    initDesignThumbs();
    initLoginRecover();
    initAddresses();
    initReveal();
    initCategoryPicker();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  global.LagomloCart = Cart;
})(window);
